import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import { API_URL } from '../../config/api';
import useRazorpayScript from '../../hooks/useRazorpayScript';
import { useAuth } from '../../context/AuthContext';
import { mentorCompanies } from '../../components/home/CompanyStrip';
import ReviewSpotlight from './ReviewSpotlight';
import '../Billing/ProgramCheckout.css';
import './SessionCheckout.css';

const TZ = 'Asia/Kolkata';
const BOOKING_SECTION = '/#book-session';

const fmt = (iso, opts) => new Intl.DateTimeFormat('en-IN', { timeZone: TZ, ...opts }).format(new Date(iso));
// What the visitor gets, repeated here to remind them why they're paying
const outcomes = [
  'Where you stand against the roles you want',
  'Line-by-line CV review',
  'Your custom career path: what to learn, build and skip'
];

// What happens once they pay, to remove the "what if nothing happens?" worry
const nextSteps = [
  { icon: TaskAltOutlinedIcon, title: 'Instant confirmation', text: 'Your slot is locked in the moment you pay.' },
  { icon: MarkEmailReadOutlinedIcon, title: 'Invite in your inbox', text: 'A Google Calendar invite with the Meet link.' },
  { icon: VideocamOutlinedIcon, title: 'Your 30-min call', text: 'Leave with your roadmap, CV fixes and next steps.' }
];

const HOLD_MS = 15 * 60000; // matches HOLD_MINUTES on the server
const HOLD_URGENT_MS = 3 * 60000;

const whenLabel = (iso) =>
  `${fmt(iso, { weekday: 'long', day: 'numeric', month: 'long' })} · ${fmt(iso, { hour: 'numeric', minute: '2-digit', hour12: true })} IST`;
const rupees = (paise) => `₹${(paise / 100).toLocaleString('en-IN')}`;

// Countdown to the end of the slot hold, e.g. "14:59"
const useCountdown = (until) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!until) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [until]);
  if (!until) return null;
  const left = Math.max(0, new Date(until).getTime() - now);
  return { left, label: `${Math.floor(left / 60000)}:${String(Math.floor((left % 60000) / 1000)).padStart(2, '0')}` };
};

// Checkout for a held roadmap-session slot: pay with Razorpay, then return to
// the booking section, which shows the confirmation.
const SessionCheckout = ({ bookingId }) => {
  const navigate = useNavigate();
  const { user, loading: authLoading, getAccessToken } = useAuth();
  const razorpay = useRazorpayScript();
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState('');
  const [paying, setPaying] = useState(false);
  const [couponInput, setCouponInput] = useState('');
  const [couponError, setCouponError] = useState('');
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const countdown = useCountdown(booking?.status === 'pending_payment' ? booking.holdExpiresAt : null);
  const expired = booking?.status === 'expired' || (countdown && countdown.left === 0);

  const authedFetch = useCallback(async (path, options = {}) => {
    const token = await getAccessToken();
    if (!token) throw new Error('Please sign in again to continue.');
    const res = await fetch(`${API_URL}/session-bookings/${bookingId}${path}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
    });
    const json = await res.json();
    if (!res.ok || !json.success) throw Object.assign(new Error(json.message || 'Something went wrong.'), { status: res.status });
    return json.data;
  }, [bookingId, getAccessToken]);

  useEffect(() => {
    if (authLoading || !user) return;
    authedFetch('')
      .then(setBooking)
      .catch((err) => setError(err.message));
  }, [authLoading, user, authedFetch]);

  // Apply a coupon (or remove it with an empty code); the server sets the price
  const updateCoupon = async (code) => {
    setCouponError('');
    setApplyingCoupon(true);
    try {
      setBooking(await authedFetch('/coupon', { method: 'POST', body: JSON.stringify({ code }) }));
      setCouponInput('');
    } catch (err) {
      setCouponError(err.message);
      if (err.status === 410) setBooking((b) => b && { ...b, status: 'expired' });
    } finally {
      setApplyingCoupon(false);
    }
  };

  // A coupon covered the whole price: confirm without Razorpay
  const confirmFree = async () => {
    setError('');
    setPaying(true);
    try {
      const confirmed = await authedFetch('/confirm-free', { method: 'POST', body: '{}' });
      navigate(BOOKING_SECTION, { state: { bookingConfirmed: confirmed } });
    } catch (err) {
      setError(err.message);
      setPaying(false);
      if (err.status === 410) setBooking((b) => b && { ...b, status: 'expired' });
    }
  };

  const pay = async () => {
    if (booking.amountPaise === 0) return confirmFree();
    setError('');
    setPaying(true);
    try {
      const order = await authedFetch('/order', { method: 'POST', body: '{}' });
      const checkout = new window.Razorpay({
        key: order.keyId,
        amount: order.amount,
        currency: order.currency,
        order_id: order.orderId,
        name: 'Codeazy',
        description: `Roadmap session · ${whenLabel(booking.slotStart)}`,
        prefill: { name: booking.name, email: booking.emailAddress },
        theme: { color: '#a855f7' },
        handler: async (payment) => {
          try {
            const confirmed = await authedFetch('/confirm', { method: 'POST', body: JSON.stringify(payment) });
            navigate(BOOKING_SECTION, { state: { bookingConfirmed: confirmed } });
          } catch (err) {
            setError(err.message);
            setPaying(false);
          }
        },
        modal: { ondismiss: () => setPaying(false) }
      });
      checkout.on('payment.failed', (res) => {
        setError(res.error?.description || 'Payment failed. Please try again.');
        setPaying(false);
      });
      checkout.open();
    } catch (err) {
      setError(err.message);
      setPaying(false);
      if (err.status === 410) setBooking((b) => b && { ...b, status: 'expired' });
    }
  };

  const backToBooking = <Link to={BOOKING_SECTION} className="sc-pay">Back to booking</Link>;
  const price = booking ? rupees(booking.amountPaise) : '';
  const listAmount = booking ? (booking.listAmountPaise || booking.amountPaise) : 0;
  const discount = booking ? listAmount - booking.amountPaise : 0;
  const discountPercent = listAmount ? Math.round((discount / listAmount) * 100) : 0;
  const free = booking?.amountPaise === 0;
  let payLabel = `Pay ${price} & confirm my slot`;
  if (free) payLabel = paying ? 'Confirming your slot…' : 'Confirm my free session';
  else if (!razorpay.ready) payLabel = 'Loading payment…';
  else if (paying) payLabel = 'Complete payment in the Razorpay window';
  const payDisabled = paying || (!free && !razorpay.ready);

  const shell = (content) => (
    <div className="sc-page">
      <header className="sc-topbar">
        <Link to="/" className="sc-logo">Codeazy</Link>
        <span className="sc-secure"><LockOutlinedIcon sx={{ fontSize: 15 }} /> Secure checkout</span>
      </header>
      <main className="sc-main">{content}</main>
    </div>
  );

  // Loading and message states use a single simple card
  const message = (children) => shell(<div className="sc-card sc-card--message">{children}</div>);

  if (authLoading || (user && !booking && !error)) {
    return message(<div className="sc-loading" aria-busy="true"><span /><span /><span /></div>);
  }
  if (!user) {
    return message(<><p>Please sign in to complete your booking.</p>{backToBooking}</>);
  }
  if (!booking) {
    return message(<><p className="sc-error" role="alert">{error}</p>{backToBooking}</>);
  }
  if (booking.status === 'booked') {
    return message(<><p>This session is already paid for and confirmed.</p>{backToBooking}</>);
  }

  const urgent = countdown && countdown.left < HOLD_URGENT_MS;
  const canPay = !expired;

  return shell(
    <>
      <div className="sc-card">
        {/* Left: what they're buying and what happens next */}
        <section className="sc-summary" aria-label="Your session">
          <ol className="sc-progress" aria-label="Booking progress">
            <li className="done"><span><CheckIcon sx={{ fontSize: 13 }} /></span>Time</li>
            <li className="done"><span><CheckIcon sx={{ fontSize: 13 }} /></span>Details</li>
            <li className="active"><span>3</span>Pay</li>
          </ol>

          <h1>You&apos;re one step away from your <span className="sc-gradient">roadmap</span></h1>
          <p className="sc-sub">Booking for <strong>{booking.emailAddress}</strong></p>

          <div className="sc-slot">
            <div className="sc-date" aria-hidden="true">
              <span>{fmt(booking.slotStart, { weekday: 'short' })}</span>
              <strong>{fmt(booking.slotStart, { day: 'numeric' })}</strong>
              <span>{fmt(booking.slotStart, { month: 'short' })}</span>
            </div>
            <div className="sc-slot-info">
              <strong>{fmt(booking.slotStart, { hour: 'numeric', minute: '2-digit', hour12: true })} IST</strong>
              <span>{booking.slotMinutes}-min 1:1 with a software architect</span>
              <Link to={BOOKING_SECTION} className="sc-change">Change time</Link>
            </div>
          </div>

          <div className="sc-block">
            <h2>You&apos;ll walk away with</h2>
            <ul className="sc-gets">
              {outcomes.map((o) => <li key={o}><CheckIcon sx={{ fontSize: 14 }} />{o}</li>)}
            </ul>
          </div>

          <div className="sc-block">
            <h2>{free ? 'What happens next' : 'What happens after you pay'}</h2>
            <ol className="sc-next">
              {nextSteps.map(({ icon: Icon, title, text }) => (
                <li key={title}>
                  <span className="sc-next-icon"><Icon sx={{ fontSize: 17 }} /></span>
                  <div>
                    <strong>{title}</strong>
                    <span>{text}</span>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div className="sc-mentors">
            <span>Mentors from</span>
            <ul>
              {mentorCompanies.slice(0, 5).map((c) => (
                <li key={c.name} title={c.name}><img src={c.logo} alt={c.name} loading="lazy" /></li>
              ))}
            </ul>
          </div>
        </section>

        {/* Right: the payment */}
        <section className="sc-payment" aria-label="Payment">
          <h2>Order summary</h2>
          <div className="sc-line">
            <span>1:1 roadmap session<small>{booking.slotMinutes} min · Google Meet</small></span>
            <span>{rupees(listAmount)}</span>
          </div>
          {booking.coupon ? (
            <div className="sc-line sc-line--discount">
              <span className="pc-coupon-applied">
                Coupon <code>{booking.coupon.code}</code>
                {canPay && (
                  <button type="button" onClick={() => updateCoupon('')} disabled={applyingCoupon} aria-label="Remove coupon">
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </button>
                )}
              </span>
              <span>−{rupees(booking.coupon.amountOffPaise)}</span>
            </div>
          ) : discount > 0 && (
            <div className="sc-line sc-line--discount">
              <span>Discount ({discountPercent}% off)</span>
              <span>−{rupees(discount)}</span>
            </div>
          )}
          <div className="sc-total">
            <span>Total</span>
            <strong>{free ? 'Free' : price}</strong>
          </div>
          {!booking.coupon && canPay && (
            <form
              className="pc-coupon"
              onSubmit={(e) => {
                e.preventDefault();
                // Apply with an empty box points them at the field instead
                if (couponInput.trim()) updateCoupon(couponInput.trim());
                else e.currentTarget.querySelector('input').focus();
              }}
            >
              <LocalOfferOutlinedIcon sx={{ fontSize: 18 }} />
              <input
                value={couponInput}
                onChange={(e) => { setCouponInput(e.target.value.toUpperCase()); setCouponError(''); }}
                placeholder="Have a coupon? Enter code"
                aria-label="Coupon code"
                autoCapitalize="characters"
              />
              <button type="submit" disabled={applyingCoupon}>
                {applyingCoupon ? 'Checking…' : 'Apply'}
              </button>
            </form>
          )}
          {couponError && <p className="pc-coupon-error" role="alert">{couponError}</p>}
          {discount > 0 && <p className="sc-saving">You save {rupees(discount)}</p>}

          {canPay ? (
            <>
              {countdown && (
                <div className={`sc-hold ${urgent ? 'urgent' : ''}`}>
                  <div className="sc-hold-text">
                    <span>Slot held for you</span>
                    <strong>{countdown.label}</strong>
                  </div>
                  <div className="sc-holdbar" aria-hidden="true">
                    <span style={{ width: `${Math.min(100, (countdown.left / HOLD_MS) * 100)}%` }} />
                  </div>
                </div>
              )}

              {error && <p className="sc-error" role="alert">{error}</p>}
              {!free && razorpay.failed && <p className="sc-error" role="alert">Could not load the payment window. Please refresh.</p>}

              <button type="button" className="sc-pay" onClick={pay} disabled={payDisabled}>
                {free ? <CheckIcon sx={{ fontSize: 18 }} /> : <LockOutlinedIcon sx={{ fontSize: 18 }} />}
                {payLabel}
              </button>

              {free ? (
                <ul className="sc-trust">
                  <li><BoltOutlinedIcon sx={{ fontSize: 14 }} /> Instant confirmation, no payment needed</li>
                </ul>
              ) : (
                <>
                  <ul className="sc-trust">
                    <li><LockOutlinedIcon sx={{ fontSize: 14 }} /> Secured by Razorpay</li>
                    <li><BoltOutlinedIcon sx={{ fontSize: 14 }} /> Instant confirmation</li>
                  </ul>
                  <ul className="sc-methods" aria-label="Payment methods">
                    <li>UPI</li>
                    <li>Cards</li>
                    <li>Net banking</li>
                  </ul>
                </>
              )}
            </>
          ) : (
            <div className="sc-expired">
              <p className="sc-error" role="alert">Your slot hold has expired. Please pick a time again.</p>
              <Link to={BOOKING_SECTION} className="sc-pay">Pick another time</Link>
            </div>
          )}

          <ReviewSpotlight />
        </section>
      </div>

      {/* Phones: total and Pay always one tap away */}
      {canPay && (
        <div className="sc-paybar">
          <div>
            <strong>{free ? 'Free' : price}</strong>
            {countdown && <span className={urgent ? 'urgent' : ''}>Held · {countdown.label}</span>}
          </div>
          <button type="button" className="sc-pay" onClick={pay} disabled={payDisabled}>
            {free ? <CheckIcon sx={{ fontSize: 17 }} /> : <LockOutlinedIcon sx={{ fontSize: 17 }} />}
            {free ? (paying ? 'Confirming…' : 'Confirm free session') : (paying ? 'Finish in Razorpay' : `Pay ${price}`)}
          </button>
        </div>
      )}
    </>
  );
};

export default SessionCheckout;
